<template>
  <div class="card shadow-sm av-card mb-4">
    <div class="card-body">
      <div class="d-flex align-items-center justify-content-between flex-wrap" style="gap: 8px;">
        <div>
          <h6 class="mb-0 font-weight-bold"><i class="fe fe-clock mr-1"></i>{{ title }}</h6>
          <small class="text-muted">Patients can only be booked into these times, so there are no clashes.</small>
        </div>
        <button v-if="!editing" type="button" class="btn btn-sm btn-outline-primary" @click="startEdit">
          <i class="fe fe-edit-2 mr-1"></i>Edit hours
        </button>
      </div>

      <!-- Summary -->
      <div v-if="!editing" class="mt-2">
        <div v-if="!hours.length" class="alert alert-warning small py-2 mb-2">
          No hours set yet: nobody can book a consultation with you until you add them.
        </div>
        <div v-for="d in days" :key="d.value" class="av-row" v-show="hoursOf(d.value).length">
          <span class="av-day">{{ d.label }}</span>
          <span>
            <span v-for="h in hoursOf(d.value)" :key="h.id" class="av-chip">{{ h.start_time.slice(0, 5) }}–{{ h.end_time.slice(0, 5) }} · {{ h.slot_minutes }} min</span>
          </span>
        </div>
      </div>

      <!-- Editor -->
      <form v-else class="mt-3" @submit.prevent="save">
        <div v-for="(row, i) in draft" :key="row.key" class="form-row align-items-end av-edit-row">
          <div class="form-group col-6 col-md-3">
            <label :for="'av-day-' + row.key" class="small font-weight-bold">Day</label>
            <select :id="'av-day-' + row.key" v-model.number="row.weekday" class="form-control form-control-sm">
              <option v-for="d in days" :key="d.value" :value="d.value">{{ d.label }}</option>
            </select>
          </div>
          <div class="form-group col-6 col-md-2">
            <label :for="'av-from-' + row.key" class="small font-weight-bold">From</label>
            <input :id="'av-from-' + row.key" v-model="row.start_time" type="time" class="form-control form-control-sm" required />
          </div>
          <div class="form-group col-6 col-md-2">
            <label :for="'av-to-' + row.key" class="small font-weight-bold">To</label>
            <input :id="'av-to-' + row.key" v-model="row.end_time" type="time" class="form-control form-control-sm" required />
          </div>
          <div class="form-group col-6 col-md-3">
            <label :for="'av-slot-' + row.key" class="small font-weight-bold">Each consultation</label>
            <select :id="'av-slot-' + row.key" v-model.number="row.slot_minutes" class="form-control form-control-sm">
              <option v-for="m in [10, 15, 20, 30, 45, 60]" :key="m" :value="m">{{ m }} minutes</option>
            </select>
          </div>
          <div class="form-group col-12 col-md-2">
            <button type="button" class="btn btn-sm btn-link text-danger" :aria-label="'Remove period ' + (i + 1)" @click="draft.splice(i, 1)">
              <i class="fe fe-trash-2"></i> Remove
            </button>
          </div>
        </div>
        <div class="d-flex flex-wrap" style="gap: 6px;">
          <button type="button" class="btn btn-sm btn-light" @click="addRow"><i class="fe fe-plus mr-1"></i>Add period</button>
          <button type="button" class="btn btn-sm btn-light" @click="weekdaysPreset">Mon–Fri 9:00–17:00</button>
        </div>
        <div v-if="error" class="alert alert-danger small py-2 mt-2">{{ error }}</div>
        <div class="d-flex mt-3" style="gap: 6px;">
          <button type="submit" class="btn btn-sm btn-primary" :disabled="saving">Save hours</button>
          <button type="button" class="btn btn-sm btn-light" @click="editing = false">Cancel</button>
        </div>
      </form>

      <!-- Days off -->
      <div class="mt-3 pt-3 av-off">
        <div class="font-weight-bold small mb-2">Days off</div>
        <span v-for="b in blocked" :key="b.id" class="av-chip av-chip--off">
          {{ b.date }}<span v-if="b.reason"> · {{ b.reason }}</span>
          <button type="button" class="av-x" :aria-label="'Remove day off ' + b.date" @click="unblock(b)"><i class="fe fe-x"></i></button>
        </span>
        <form class="d-flex flex-wrap align-items-end mt-1" style="gap: 6px;" @submit.prevent="block">
          <label :for="'av-off-' + uid" class="sr-only">Day off</label>
          <input :id="'av-off-' + uid" v-model="offDate" type="date" class="form-control form-control-sm" style="max-width: 170px;" :min="today" required />
          <label :for="'av-offr-' + uid" class="sr-only">Reason</label>
          <input :id="'av-offr-' + uid" v-model="offReason" class="form-control form-control-sm" style="max-width: 220px;" placeholder="Reason (optional)" />
          <button type="submit" class="btn btn-sm btn-outline-secondary">Add day off</button>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
let counter = 0;
let rowKey = 0;
const DAYS = [
  { value: 1, label: "Monday" }, { value: 2, label: "Tuesday" }, { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" }, { value: 5, label: "Friday" }, { value: 6, label: "Saturday" }, { value: 0, label: "Sunday" },
];

// Weekly consultation hours and days off of a telemedicine doctor (default: the signed-in user).
export default {
  name: "AvailabilityCard",
  props: {
    userId: { type: [Number, String], default: null },
    title: { type: String, default: "My consultation hours" },
  },
  data() {
    return {
      uid: ++counter,
      days: DAYS,
      hours: [],
      blocked: [],
      draft: [],
      editing: false,
      saving: false,
      error: "",
      offDate: "",
      offReason: "",
      today: new Date().toISOString().slice(0, 10),
    };
  },
  created() {
    this.load();
  },
  methods: {
    params() {
      return this.userId ? { user_id: this.userId } : {};
    },
    apply(data) {
      this.hours = data.hours || [];
      this.blocked = data.blocked_dates || [];
    },
    load() {
      this.axios.get("/api/v1/auth/telemedicine/availability", { params: this.params() }).then((res) => this.apply(res.data));
    },
    hoursOf(day) {
      return this.hours.filter((h) => Number(h.weekday) === day);
    },
    startEdit() {
      this.draft = this.hours.map((h) => ({ key: ++rowKey, weekday: Number(h.weekday), start_time: h.start_time.slice(0, 5), end_time: h.end_time.slice(0, 5), slot_minutes: Number(h.slot_minutes) }));
      if (!this.draft.length) this.addRow();
      this.error = "";
      this.editing = true;
    },
    addRow() {
      this.draft.push({ key: ++rowKey, weekday: 1, start_time: "09:00", end_time: "13:00", slot_minutes: 30 });
    },
    weekdaysPreset() {
      this.draft = [1, 2, 3, 4, 5].map((d) => ({ key: ++rowKey, weekday: d, start_time: "09:00", end_time: "17:00", slot_minutes: 30 }));
    },
    errorText(err, fallback) {
      const d = (err.response && err.response.data) || {};
      return d.errors ? Object.values(d.errors).flat().join(" ") : d.message || fallback;
    },
    save() {
      this.saving = true;
      this.error = "";
      const hours = this.draft.map(({ weekday, start_time, end_time, slot_minutes }) => ({ weekday, start_time, end_time, slot_minutes }));
      this.axios
        .put("/api/v1/auth/telemedicine/availability", { ...this.params(), hours })
        .then((res) => {
          this.apply(res.data);
          this.editing = false;
          this.$toasted.success("Hours saved", { position: "top-center", duration: 3000 });
        })
        .catch((err) => (this.error = this.errorText(err, "Could not save the hours")))
        .finally(() => (this.saving = false));
    },
    block() {
      this.axios
        .post("/api/v1/auth/telemedicine/availability/blocked-dates", { ...this.params(), date: this.offDate, reason: this.offReason || null })
        .then((res) => {
          this.apply(res.data);
          this.offDate = "";
          this.offReason = "";
        })
        .catch((err) => this.$toasted.error(this.errorText(err, "Could not add the day off"), { position: "top-center", duration: 4000 }));
    },
    unblock(b) {
      this.axios
        .delete(`/api/v1/auth/telemedicine/availability/blocked-dates/${b.id}`, { params: this.params() })
        .then((res) => this.apply(res.data));
    },
  },
};
</script>

<style scoped>
.av-card { border-radius: 12px; border: none; border-left: 4px solid #7c3aed; }
.av-row { display: flex; gap: 12px; padding: 4px 0; font-size: .88rem; }
.av-day { width: 96px; font-weight: 600; color: #374151; }
.av-chip { display: inline-flex; align-items: center; gap: 4px; background: #ede9fe; color: #5b21b6; border-radius: 20px; padding: 2px 10px; font-size: .78rem; font-weight: 600; margin: 0 6px 6px 0; }
.av-chip--off { background: #fee2e2; color: #991b1b; }
.av-x { border: none; background: none; color: inherit; padding: 0 0 0 4px; line-height: 1; }
.av-off { border-top: 1px solid #f1f5f9; }
.av-edit-row { background: #f8fafc; border-radius: 8px; padding: 6px 4px 0; margin: 0 0 8px; }
</style>
